import { useEffect, useEffectEvent, useRef } from 'react';
import type { FieldPath, FieldValues, UseFormClearErrors, UseFormSetError } from 'react-hook-form';

import type { FormServerErrors } from './Form';

interface UseFormServerErrorsSyncArgs<TFieldValues extends FieldValues> {
  serverErrors: FormServerErrors | undefined;
  clearErrors: UseFormClearErrors<TFieldValues>;
  setError: UseFormSetError<TFieldValues>;
}

export function useFormServerErrorsSync<TFieldValues extends FieldValues>({
  serverErrors,
  clearErrors,
  setError,
}: UseFormServerErrorsSyncArgs<TFieldValues>): void {
  const serverErrorFieldsRef = useRef<Set<string>>(new Set());

  // `clearErrors`/`setError` mutate react-hook-form's own internal error store (a plain
  // subscriber-based Subject outside React's state), not a React state setter, so they can't be
  // called synchronously during render the way a `useState` setter can -- doing so risks
  // duplicate or torn writes to that external store under Strict Mode / concurrent rendering.
  // This is exactly the "child owns an external subscription the parent can't subscribe to" case
  // the no-pass-live-state-to-parent / no-pass-data-to-parent rules call out as a valid exception,
  // so the sync stays in an effect. `useEffectEvent` keeps `clearErrors`/`setError` out of the
  // dependency array so the effect only re-runs when `serverErrors` itself changes.
  const syncServerErrors = useEffectEvent((nextServerErrors: FormServerErrors | undefined) => {
    const nextServerErrorFields = new Set(Object.keys(nextServerErrors ?? {}));
    for (const field of serverErrorFieldsRef.current) {
      if (!nextServerErrorFields.has(field)) clearErrors(field as FieldPath<TFieldValues>);
    }
    for (const [field, messages] of Object.entries(nextServerErrors ?? {})) {
      const message = messages[0];
      if (message) setError(field as FieldPath<TFieldValues>, { type: 'server', message });
    }
    serverErrorFieldsRef.current = nextServerErrorFields;
  });

  useEffect(() => {
    syncServerErrors(serverErrors);
  }, [serverErrors]);
}
